import { useEffect, useRef, useState } from 'react';

type ChartCopyStatus = 'idle' | 'success' | 'error';

export function useChartCopy(backgroundColor: string) {
  const [status, setStatus] = useState<ChartCopyStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopyToClipboard = async (chartElement: HTMLElement | null) => {
    if (!chartElement) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    try {
      const { toBlob } = await import('html-to-image');
      const blobPromise = toBlob(chartElement, {
        skipFonts: true,
        backgroundColor,
        filter: (node: HTMLElement) =>
          !('excludeFromCapture' in (node.dataset ?? {})),
      }).then(blob => {
        if (!blob) throw new Error('Failed to generate image');
        return blob;
      });

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blobPromise }),
      ]);

      setStatus('success');
    } catch {
      setStatus('error');
    }
    timeoutRef.current = setTimeout(() => setStatus('idle'), 2000);
  };

  return { status, handleCopyToClipboard };
}
